import { Theme, useMediaQuery } from '@mui/material';
import React from 'react';
import {
  CardBody,
  CardHeader,
  DateLabel,
  DateValue,
  HeaderInfo,
  ItemCount,
  MobileCardBody,
  MobileCardHeader,
  MobileOrderCardContainer,
  MobileOrderFooter,
  MobileQuickInfo,
  MoreItemsIndicator,
  OrderCardContainer,
  OrderDate,
  OrderFooter,
  OrderRef,
  ProductImage,
  ProductPreview,
  QuickInfo,
  StatusBadge,
  StatusRow,
  TopRow,
  TotalAmount,
  VendorImage,
  ViewDetailsButton
} from './styles';

interface Product {
  productId: number;
  productName: string;
  price: number;
  quantity: number;
  images: string[];
}

interface OrderItem {
  id: number;
  customer: {
    id: number;
    name: string;
  };
  orderRef: string;
  paymentTrxRef: string;
  totalAmount: number;
  shippingFee: number;
  orderStatus: string;
  paymentStatus: string;
  paymentType: string;
  paymentMode: string;
  orderDate: string;
  deliveryMethod: string;
  deliveryStatus: string;
  products: Product[];
  vendorName: string;
  vendorLogo: string;
  vendorCategory: string;
  totalItems: number;
}

interface Props {
  item: OrderItem;
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount: number) => {
  return `₦${amount.toLocaleString()}`;
};

export const OrderCard: React.FC<Props> = ({ item }) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  // Use mobile components for smaller screens
  const Container = isMobile ? MobileOrderCardContainer : OrderCardContainer;
  const Header = isMobile ? MobileCardHeader : CardHeader;
  const Body = isMobile ? MobileCardBody : CardBody;
  const QuickInfoContainer = isMobile ? MobileQuickInfo : QuickInfo;
  const Footer = isMobile ? MobileOrderFooter : OrderFooter;

  return (
    <Container>
      <Header>
        <VendorImage>
          <img src={item.vendorLogo || '/graphics/madina.svg'} alt={item.vendorName} />
        </VendorImage>

        <HeaderInfo>
          <TopRow>
            {/* <VendorInfo>
              <VendorName>{item.vendorName}</VendorName>
            </VendorInfo> */}
            <OrderRef>{item.orderRef}</OrderRef>
          </TopRow>

          <StatusRow>
            <StatusBadge status={item.orderStatus}>{item.orderStatus}</StatusBadge>
          </StatusRow>
        </HeaderInfo>
      </Header>

      <Body>
        <QuickInfoContainer>
          <OrderDate>
            <DateLabel>Order Date</DateLabel>
            <DateValue>{formatDate(item.orderDate)}</DateValue>
          </OrderDate>
          <ItemCount>
            {item.totalItems} item{item.totalItems > 1 ? 's' : ''}
          </ItemCount>
        </QuickInfoContainer>

        {/* Product preview - show first 5 products */}
        <ProductPreview>
          {item.products.slice(0, 5).map(product => (
            <ProductImage key={product.productId}>
              <img src={product.images[0] || '/graphics/placeholder.svg'} alt={product.productName} />
            </ProductImage>
          ))}
          {item.products.length > 5 && <MoreItemsIndicator>+{item.products.length - 5}</MoreItemsIndicator>}
        </ProductPreview>

        <Footer>
          <TotalAmount>{formatCurrency(item.totalAmount)}</TotalAmount>
          <ViewDetailsButton to={`${item.id}`}>
            {isMobile ? 'Details' : 'View Details'}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9 6L15 12L9 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ViewDetailsButton>
        </Footer>
      </Body>
    </Container>
  );
};
